import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'

import { CurrencyInput } from './currency-input/currency-input'
import { SwapInputSeparator } from './wallet-swap-input-separator'

import type { WalletSwapInputModel } from '../../atoms/reatom-wallet-swap-input'
import type { WalletSwapQuoteModel } from '../../atoms/reatom-wallet-swap-quote'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  inputModel: WalletSwapInputModel
  quoteModel: WalletSwapQuoteModel
  onChangeBaseCurrency: () => void
  onChangeQuoteCurrency: () => void
} & ClassNameProps

export const WalletSwapInput: React.FC<Props> = ({
  inputModel,
  quoteModel,
  onChangeBaseCurrency,
  onChangeQuoteCurrency,
  className,
}) => {
  const [outAmountRaw] = useAtom(inputModel.outCurrencyInputModel.amountRawAtom)
  const [outCurrency] = useAtom(inputModel.outCurrencyAtom)
  const [outBalance] = useAtom(inputModel.outCurrencyInputModel.balanceAtom)
  const setOutAmountRaw = useAction(
    inputModel.outCurrencyInputModel.setAmountRawAction,
  )

  const [inAmountRaw] = useAtom(inputModel.inCurrencyInputModel.amountRawAtom)
  const [isMaxAmount] = useAtom(inputModel.inCurrencyInputModel.isMaxAmountAtom)
  const [inCurrency] = useAtom(inputModel.inCurrencyAtom)
  const [inBalance] = useAtom(inputModel.inCurrencyInputModel.balanceAtom)
  const setInAmountRaw = useAction(
    inputModel.inCurrencyInputModel.setAmountRawAction,
  )

  const [inputType, setInputType] = useAtom(inputModel.inputTypeAtom)
  const [inCurrencyPriceUsd] = useAtom(quoteModel.inCurrencyPriceUsdAtom)
  const [outCurrencyPriceUsd] = useAtom(quoteModel.outCurrencyPriceAtom)

  const [quoteState] = useAtom(quoteModel.quoteStateAtom)

  const switchCurrencies = useAction(inputModel.switchCurrenciesAction)

  const isQuoteLoading = AsyncStateN.isLoading(quoteState)
  const isBaseInputLoading = inputType === 'EXACT_OUTPUT' && isQuoteLoading
  const isQuoteInputLoading = inputType === 'EXACT_INPUT' && isQuoteLoading

  return (
    <Root className={className}>
      <CurrencyInput
        isMaxAmount={isMaxAmount}
        value={inAmountRaw}
        direction="source"
        currency={inCurrency}
        placeholderColor="blue"
        loading={isBaseInputLoading}
        onCurrencyClick={() => {
          onChangeBaseCurrency()
        }}
        onChange={(value) => {
          setInputType('EXACT_INPUT')
          setInAmountRaw(value)
        }}
        onClick={(e) => {
          if (!inCurrency) {
            onChangeBaseCurrency()
            e.stopPropagation()
          }
        }}
        balance={inBalance}
        currencyPriceUsd={inCurrencyPriceUsd}
      />

      <SwapInputSeparator onSwitchCurrencies={switchCurrencies} />

      <CurrencyInput
        isMaxAmount={isMaxAmount}
        value={outAmountRaw}
        direction="target"
        currency={outCurrency}
        placeholderColor="green"
        loading={isQuoteInputLoading}
        onCurrencyClick={() => {
          onChangeQuoteCurrency()
        }}
        onChange={(value) => {
          setInputType('EXACT_OUTPUT')
          setOutAmountRaw(value)
        }}
        onClick={(e) => {
          if (!outCurrency) {
            onChangeQuoteCurrency()
            e.stopPropagation()
          }
        }}
        balance={outBalance}
        currencyPriceUsd={outCurrencyPriceUsd}
      />
    </Root>
  )
}

const Root = styled.div`
  --swap-input-bg: var(--appearanceA-7);

  position: relative;
  border-radius: 16px;
  overflow: hidden;

  display: grid;
  grid-template-rows: auto 5px auto;
`
